import { Configuration, FrontendApi, Session } from '@ory/kratos-client';
import debug from 'debug';
import { makeAutoObservable, runInAction } from 'mobx';

import { AxiosQueryModel, makeAxiosQuery } from '@r-client/shared/data/client';
import { getCookie } from '@r-client/shared/util/core';

import {
  GlobalUser,
  GlobalViewer,
  Guest,
  IGlobalAuthModel,
  IGlobalAuthModelOpts,
  ISessionModel,
  MaybeValue,
} from './interface';

const log = debug('feature:global-auth');
export const SESSION_INDICATOR_COOKIE_NAME = 'global_auth_session_logged_in';

const createGuest = (): Guest => ({
  isAuthenticated: false,
  isAdmin: false,
  info: undefined,
});

const createUser = (session: Session): GlobalUser => ({
  isAuthenticated: true,
  isAdmin: false,
  info: session ?? {},
});

export const hasGlobalAuthSession = () => {
  return getCookie(SESSION_INDICATOR_COOKIE_NAME) === 'true';
};

export const isGlobalAuth = (
  model: ISessionModel<unknown>
): model is GlobalAuthModel => {
  return 'ory' in model;
};

export class GlobalAuthModel implements IGlobalAuthModel {
  private _ory: FrontendApi;
  private sessionQuery: AxiosQueryModel<Session>;
  private hasInitialFetch = false;
  private sessionBeingRefreshed: Session | undefined = undefined;

  constructor(opts: IGlobalAuthModelOpts) {
    makeAutoObservable(this);

    this._ory = new FrontendApi(
      new Configuration({
        basePath: opts.globalAuthBaseUrl,
        baseOptions: {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        },
      })
    );

    log('GlobalAuthModel initialized');

    this.sessionQuery = makeAxiosQuery(() => this.ory.toSession());
  }

  get ory(): FrontendApi {
    return this._ory;
  }

  reloadSession = async (): Promise<void> => {
    log('Session reload forcefully!');
    this.hasInitialFetch = true;
    this.sessionBeingRefreshed =
      this.sessionQuery.value ?? this.sessionBeingRefreshed;
    try {
      await this.sessionQuery.fetch();
    } finally {
      runInAction(() => {
        this.sessionBeingRefreshed = undefined;
      });
    }
  };

  private ensureSessionFetched = () => {
    if (!this.hasInitialFetch) {
      log('Lazy loading session on first viewer access');
      this.hasInitialFetch = true;
      this.sessionQuery.fetch();
    }
  };

  get viewer(): MaybeValue<GlobalViewer> {
    this.ensureSessionFetched();

    if (this.sessionQuery.state === 'error') {
      return createGuest();
    }

    const session = this.session;
    if (!session) {
      return undefined;
    }

    return session.id ? createUser(session) : createGuest();
  }

  get isLoading(): boolean {
    this.ensureSessionFetched();
    return this.sessionQuery.state === 'pending';
  }

  get errors() {
    return this.sessionQuery.error;
  }

  get sessionExpired(): boolean {
    return (
      this.sessionQuery.state === 'error' &&
      this.sessionQuery.error?.response?.status === 401 &&
      hasGlobalAuthSession()
    );
  }
  get session(): MaybeValue<Session> {
    return this.sessionQuery.value ?? this.sessionBeingRefreshed;
  }
}
